import {
  memo,
  type CSSProperties,
  useCallback,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Blocks,
  Box,
  BookOpen,
  Braces,
  CircleDot,
  Database,
  History,
  ChevronRight,
  Layers,
  Maximize,
  Minus,
  Plus,
  Search,
  ShieldCheck,
  X,
  Trash2,
  Workflow,
  Zap,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import {
  type Pan,
  appliesTo,
  evidence,
  fitToView,
  ROW_GAP,
  lanes,
  panForZoom,
  toWorld,
  violationsFor,
} from "@/lib/scoring/layout";
import {
  type CanvasGraph,
  addCard,
  connect,
  disconnect,
  graphOf,
  moveCard,
  refusal,
  refusalFor,
  removeCard,
  wiringOf,
} from "@/lib/scoring/graph";
import type {
  AnyNode,
  CanvasEdge,
  CommandNode,
  ContextNode,
  JsonSchema,
  NodeKind,
  ScoringProgram,
  Selected,
  TriggerNode,
  ValidationNode,
  Variant,
} from "@/lib/scoring/types";

import { ReferencePane } from "./ReferencePane";

import styles from "./ProgramCanvas.module.css";

type Point = { x: number; y: number };
type NodeEntry = { kind: NodeKind; node: AnyNode };

/** Everything a card can ask the canvas to do. Bundled so one stable object
 *  can be handed to every card instead of nine fresh closures each. */
interface CardHandlers {
  pick: (kind: NodeKind, node: AnyNode) => void;
  hover: (cardKey: string | null) => void;
  down: (event: ReactPointerEvent<HTMLButtonElement>, node: AnyNode) => void;
  move: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  up: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  startWire: (event: ReactPointerEvent<HTMLElement>, entry: NodeEntry, cardKey: string) => void;
  finishWire: (entry: NodeEntry, cardKey: string) => void;
  land: (componentId: string, intoCard: string) => void;
  remove: (cardKey: string) => void;
}

const CARD_WIDTH = 216;
const CARD_HEIGHT = 104;
const WORLD_PADDING = 72;
/**
 * How far above a card's top edge its in-port sticks out — `.portIn`'s `top`.
 *
 * The line stops here rather than at the card, so the arrowhead lands *on* the
 * connector instead of under it. The port is an opaque half-pill inside the
 * card, and the card paints above the edge layer, so an arrow drawn to the
 * card's own edge was a head with no point: the last six pixels of it were
 * behind the very thing it was pointing at.
 */
const PORT_LIFT = 6;

/** How wide the config panel may be, and where it starts.
 *
 * The floor is the schema tree's: a field row is a name, a type, a tick and a
 * delete, and every level of nesting takes another 24px off the name — below
 * 360 a two-deep object has nowhere left to put one. The ceiling leaves the
 * canvas something to be; wider than this is a page with a canvas in the corner.
 */
const PANEL_MIN = 360;
const PANEL_MAX = 860;
const PANEL_DEFAULT = 420;
const PANEL_KEY = "omnium.builder.panelWidth";
const clampPanel = (value: number) => Math.min(PANEL_MAX, Math.max(PANEL_MIN, Math.round(value)));

/** The menu side. Narrower floor — a list of four tiles needs far less than a
 *  nested schema — and a default per pane, because a reference document and a
 *  context function are not read at menu width. */
const MENU_MIN = 260;
const MENU_MAX = 820;
const MENU_KEY = "omnium.builder.menuWidth";
const MENU_DEFAULT: Record<string, number> = {
  nodes: 318,
  components: 348,
  layers: 318,
  // The reference is a document with two-column field tables in it. At 428 the
  // type and the sentence stacked on every row and a root's field list turned
  // into a column of fragments; 560 is where they sit side by side.
  reference: 560,
  context: 480,
};
const clampMenu = (value: number) => Math.min(MENU_MAX, Math.max(MENU_MIN, Math.round(value)));
/** Half size is as far out as it goes. Past that a card is a grey rectangle
 *  with unreadable text on it — you can no longer tell what you are looking at,
 *  which is the point at which zooming out has stopped helping. */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;

/** How hard a pinch bites. Exponential rather than linear so the same gesture
 *  covers the same proportion of the range at 25% as at 200% — a linear step
 *  crawls when zoomed out and lurches when zoomed in. */
const PINCH = 0.0075;
const WHEEL_STEP = 1.12;

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

/** How far apart the canvas dots are at 1:1, in pixels.
 *
 * Also written in `.canvas`'s `background-size`, which is what shows before
 * React attaches. It is here because the grid has to scale with the zoom, and a
 * stylesheet cannot know the zoom. The two must agree — they disagreed for a
 * while, and the stylesheet lost every time. */
const DOT_GRID = 22;

/** The levels worth naming, inside the range the canvas allows.
 *
 * Each carries the icon for what it does to the view rather than a decoration:
 * out, back to true size, in. 150 and 200 share one because they are the same
 * move at two depths, and the number is what tells them apart. */
const ZOOM_PRESETS = [
  { value: 50, icon: ZoomOut, shortcut: null },
  { value: 100, icon: Search, shortcut: "⌘0" },
  { value: 150, icon: ZoomIn, shortcut: null },
  { value: 200, icon: ZoomIn, shortcut: null },
] as const;

/** Whole pixels. A pan of 72.4px puts every edge and glyph on a half pixel,
 *  which the browser resolves by blending — the canvas reads as soft for no
 *  reason anyone can point at. */
const whole = (p: Pan): Pan => ({ x: Math.round(p.x), y: Math.round(p.y) });

/** Things that handle their own pointer: cards, buttons, the connect handle.
 *  Dragging one of these is never a pan of the background. */
const OWN_POINTER =
  "button, a, select, input, textarea, [role='button'], [data-node-id], [data-edge], [data-edge-menu]";



/**
 * What the label above a card says.
 *
 * Not the kind's own name in every case: "Derive" is what the document calls
 * the node and "Fold" is what it does to the log, and the card is read far more
 * often than the schema is.
 */
const KIND_TAG: Record<NodeKind, string> = {
  command: "Command",
  validation: "Rule",
  compute: "Compute",
  trigger: "Action",
  context: "Read model",
};

/** What each pane calls itself. A table rather than a ladder of ternaries — the
 *  ladder was four deep before Components would have made it five. */
const PANE_HEAD: Record<string, { name: string; hint: string }> = {
  layers: { name: "Layers", hint: "Every command, and what it runs" },
  reference: { name: "Reference", hint: "What you can write, and what is in scope" },
  context: { name: "Context", hint: "One per program — what a scoring screen reads" },
};


const DRAG_NODE = "application/x-scoring-node";
/** A component being dragged from its pane onto a command. Its own type rather
 *  than reusing DRAG_NODE, because that one means "move this card" and this one
 *  means "run this here" — and a drop handler that cannot tell them apart would
 *  move the card instead of attaching it. */
const DRAG_COMPONENT = "application/x-scoring-component";

export interface CanvasProps {
  program: ScoringProgram;
  /** The program version's id — what the Reference pane asks the server about. */
  programId?: string;
  /** What the author calls this workflow; falls back to "Version N". */
  title?: string;
  /** Whose program this is, and which version — both from the row rather than
   *  the document, because an empty draft's document carries neither. */
  sport: string | null;
  version: number;
  /** Where the back arrow goes, and what the crumb says. Defaults to the
   *  sport's scoring list, or to Workflows for a workflow no sport owns. */
  back?: { href: string; label: string };
  toolbar?: ReactNode;
  editorFor?: (kind: NodeKind, node: AnyNode) => ReactNode;
  banner?: ReactNode;
  flagged?: Set<string>;
  /** How many problems the last Validate found. Null until one has run, when
   *  the header falls back to what the document itself says. */
  reported?: number | null;
  selectedId?: string | null;
  /** What this sport's nodes may be limited to — the lens, and the legal values
   *  of `appliesTo`. Empty hides the lens entirely. */
  variants?: Variant[];
  onSelect?: (kind: NodeKind, node: AnyNode) => void;
  /** Make a shared node from the Components pane. It still gets a place on the
   *  canvas: a component is a card like any other, and one created at the world
   *  origin is a card the author has to go looking for. */
  onAddComponent?: (kind: NodeKind, point: Point) => Promise<void> | void;
}

export function ProgramCanvas({
  program,
  programId,
  title,
  sport,
  version,
  back,
  toolbar,
  editorFor,
  banner,
  flagged,
  reported,
  selectedId,
  onSelect,
}: CanvasProps) {
  const nodeEntries = useMemo(() => entries(program), [program]);
  const entryById = useMemo(
    () => new Map(nodeEntries.map((entry) => [entry.node.id, entry])),
    [nodeEntries],
  );
  const kindOf = useCallback(
    (nodeId: string) => entryById.get(nodeId)?.kind ?? null,
    [entryById],
  );

  /**
   * The picture, exactly as it is stored.
   *
   * Not computed from anything. Every card here was put there by somebody, and
   * every line was drawn by somebody — which is the whole reason nothing on
   * this canvas moves or rewires itself any more. A card's position is its own
   * `x`/`y`, so wiring cannot renumber it and a lane cannot slide.
   */
  const stored = useMemo(() => graphOf(program), [program]);
  /**
   * The graph a gesture has just made, before the save comes back.
   *
   * Held so a drag or a connect draws at once instead of after a round trip.
   * It wins for as long as something is still in flight or a card is under the
   * pointer, and the server's copy takes over the moment nothing is.
   *
   * It used to be stamped with the stored graph it was drawn on and discarded
   * as soon as `stored` moved — but `stored` moves once per *save*, not once
   * per gesture, so a second gesture made while the first was saving flickered
   * back to the first and then forward again. Worse, a card being dragged
   * snapped back mid-drag if any unrelated save happened to land.
   */
  const [pending, setPending] = useState<{ from: CanvasGraph; graph: CanvasGraph } | null>(null);
  const [saving] = useState(0);
  const draw = useCallback(
    (next: CanvasGraph) => setPending({ from: stored, graph: next }),
    [stored],
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  /**
   * What the canvas is drawing.
   *
   * The local picture wins while it is still the one that was drawn on (`from`
   * still matches), or while a save of ours is in flight — that second clause
   * is what stops a second gesture flickering back to the first while the
   * first is saving.
   *
   * And it *loses* the moment the stored picture moves on for some other
   * reason, which is the case that bit: making a node writes the document
   * without going through here, so a local picture drawn before it is missing
   * that node — and a card held under the pointer with a stale local picture
   * made the new node blink out of existence.
   */
  const graph =
    pending && (pending.from === stored || saving > 0) ? pending.graph : stored;

  /** The cards the canvas draws, each resolved to the node it shows. A card
   *  whose node has gone is simply not drawn. */
  const cards = useMemo(
    () =>
      graph.cards.flatMap((card) => {
        const entry = entryById.get(card.node);
        return entry ? [{ card, kind: entry.kind, node: entry.node }] : [];
      }),
    [graph, entryById],
  );
  const cardById = useMemo(() => new Map(cards.map((one) => [one.card.id, one])), [cards]);
  /** How many cards each node has. Counted once, because the render loop asks
   *  it per card and a scan per card is a scan of everything by everything. */
  /**
   * The card handlers, with identities that do not change.
   *
   * A card is redrawn when any of its props differs from last time, and a
   * function defined in the render body is a different function every render —
   * so without this every card in the program was redrawn on every pointer
   * move, whatever else was true. The ref always holds the newest closures, so
   * the stable wrappers behave exactly as the plain functions did.
   */
  const newest = useRef<CardHandlers | null>(null);
  const handlers = useMemo<CardHandlers>(
    () => ({
      pick: (kind, node) => newest.current!.pick(kind, node),
      hover: (key) => newest.current!.hover(key),
      down: (event, node) => newest.current!.down(event, node),
      move: (event) => newest.current!.move(event),
      up: (event) => newest.current!.up(event),
      startWire: (event, entry, key) => newest.current!.startWire(event, entry, key),
      finishWire: (entry, key) => newest.current!.finishWire(entry, key),
      land: (componentId, intoCard) => newest.current!.land(componentId, intoCard),
      remove: (key) => newest.current!.remove(key),
    }),
    [],
  );

  const cardsPerNode = useMemo(() => {
    const count = new Map<string, number>();
    for (const one of cards) count.set(one.node.id, (count.get(one.node.id) ?? 0) + 1);
    return count;
  }, [cards]);
  /** What each card reaches and what reaches it — read once, not searched per
   *  card, and the same answer the refusal messages are built from. */
  const wiring = useMemo(() => wiringOf(graph, kindOf), [graph, kindOf]);

  const [selected, setSelected] = useState<Selected | null>(() => {
    const first = nodeEntries[0];
    return first ? { kind: first.kind, node: first.node } : null;
  });
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [filter, setFilter] = useState("");
  const [pane, setPane] = useState<"context" | "layers" | "reference">("layers");

  /**
   * How tall each card actually is.
   *
   * Cards grew a variable height when they gained a description, and a line has
   * to leave from the *bottom* of the card it starts at. Measured rather than
   * predicted: the height depends on how a browser wrapped somebody's prose,
   * which is not a number this component can work out.
   *
   * A card that has not been measured yet falls back to the minimum, so the
   * first paint draws sensible lines rather than none.
   */
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({});

  const heightOf = useCallback(
    (cardKey: string) => cardHeights[cardKey] ?? CARD_HEIGHT,
    [cardHeights],
  );

  /**
   * Watch every card and keep its measured height.
   *
   * A ResizeObserver rather than a measure-after-render: a description reflows
   * when the pane beside it opens, and nothing re-renders to notice that.
   *
   * Re-subscribed only when the *set* of cards changes — not on every render.
   * It had no dependency array at all, which meant a canvas of three hundred
   * cards tore down and rebuilt three hundred observations on every frame of
   * every drag, for a set that had not changed.
   */
  const cardRoster = useMemo(() => cards.map((one) => one.card.id).join(" "), [cards]);
  useEffect(() => {
    const surface = viewportRef.current;
    if (!surface) return;

    const observer = new ResizeObserver((entries) => {
      setCardHeights((was) => {
        let next = was;
        for (const entry of entries) {
          const card = entry.target as HTMLElement;
          const key = card.dataset.card;
          // The *border* box. `contentRect` is the content box, so it leaves out
          // the card's padding and border — twenty-two pixels the line then
          // started short of, which put it inside the card instead of on the
          // port at its edge.
          const height = Math.round(
            entry.borderBoxSize?.[0]?.blockSize ?? card.offsetHeight,
          );
          if (!key || !height || was[key] === height) continue;
          if (next === was) next = { ...was };
          next[key] = height;
        }
        return next;
      });
    });

    for (const card of surface.querySelectorAll<HTMLElement>("[data-card]")) {
      observer.observe(card);
    }
    return () => observer.disconnect();
  }, [cardRoster]);


  /**
   * Layers, as the canvas draws them: a command and everything it runs.
   *
   * The pane used to list commands and stop, which meant the one place that
   * could tell you what a command is made of showed only its name — and the
   * rules and actions, the parts you actually go looking for, were reachable
   * only by finding them on the canvas.
   *
   * A search matches a command *or* any of its children, and keeps only the
   * children that matched — looking for a rule should not hand you back every
   * other rule its command happens to run.
   */
  const layers = useMemo(() => {
    const wanted = filter.trim().toLowerCase();
    const hit = (node: AnyNode) =>
      `${node.code} ${node.label ?? ""} ${node.description ?? ""}`.toLowerCase().includes(wanted);
    return lanes(program).flatMap((lane) => {
      if (!wanted) return [lane];
      if (hit(lane.command)) return [lane];
      const validations = lane.validations.filter(hit);
      const triggers = lane.triggers.filter(hit);
      if (!validations.length && !triggers.length) return [];
      return [{ ...lane, validations, triggers, missing: [] }];
    });
  }, [filter, program]);

  /** Which commands are open. Searching opens everything, because a match you
   *  cannot see is the same as no match. */
  const [openLayers, setOpenLayers] = useState<Set<string>>(new Set());
  const searching = filter.trim().length > 0;
  /** The inspector opens when you pick a node and closes when you say so. It
   *  used to be permanent furniture, holding "Select a node" over a third of the
   *  canvas for as long as nothing was selected. */
  const [inspectorOpen, setInspectorOpen] = useState(false);
  // 1, not 0.9. Any scale other than 1 resamples every glyph on the canvas, and
  // opening at 90% meant the builder looked soft before anyone touched it.
  const [zoom, setZoom] = useState(1);
  const [zoomMenu, setZoomMenu] = useState(false);
  /**
   * How wide the config panel is.
   *
   * The remembered width comes through `useSyncExternalStore` rather than an
   * effect. The server has no `localStorage`, so it renders the default and
   * hydration matches; the client's own first render already has the real
   * value, so the panel does not open at one width and jump to another.
   * `dragWidth` is whatever the current gesture says, and it wins while it
   * lasts.
   */
  const storedWidth = useSyncExternalStore(
    () => () => {},
    () => window.localStorage.getItem(PANEL_KEY),
    () => null,
  );
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const panelWidth = dragWidth ?? clampPanel(Number(storedWidth) || PANEL_DEFAULT);
  const setPanelWidth = (next: number | ((current: number) => number)) =>
    setDragWidth((current) =>
      clampPanel(typeof next === "function" ? next(current ?? panelWidth) : next),
    );

  const storedMenu = useSyncExternalStore(
    () => () => {},
    () => window.localStorage.getItem(MENU_KEY),
    () => null,
  );
  const [menuDragWidth, setMenuDragWidth] = useState<number | null>(null);
  const menuWidth =
    menuDragWidth ?? clampMenu(Number(storedMenu) || MENU_DEFAULT[pane] || MENU_DEFAULT.nodes);
  const setMenuWidth = (next: number | ((current: number) => number)) =>
    setMenuDragWidth((current) =>
      clampMenu(typeof next === "function" ? next(current ?? menuWidth) : next),
    );
  const [resizing, setResizing] = useState(false);
  /** How to end a resize that is still running. Held so leaving the page in
   *  the middle of one does not strand three window listeners and a cursor. */
  const endResizeRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endResizeRef.current?.(), []);
  /** The format lens. The control that set it left the toolbar with the rest
   *  of the format machinery — how programs map to formats is being rethought —
   *  but the plumbing stays for when that lands. */
  const lens: string | null = null;
  /** A line being drawn: where it started (which card, exactly), and where the
   *  pointer is now. */
  const [wire, setWire] = useState<{ from: NodeEntry; fromKey: string; at: Point } | null>(null);
  /** Which card the line in flight is over. Only the one under the pointer
   *  lights up: marking every card that *could* take the line told you the
   *  rules rather than where you were about to drop it. */
  const [wireOver, setWireOver] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  /**
   * The component being dragged out of its pane, if any.
   *
   * Held here rather than read off the drag event because `dragover` is the
   * only place the payload can be read during a drag, and only on the element
   * under the pointer — so a card cannot ask "am I a place this could land?"
   * until the pointer is already on it. Keeping what is in flight in state lets
   * every legal target light up the moment the drag starts, which is the
   * difference between "drop it and find out" and being shown where it goes.
   */
  const [carrying, setCarrying] = useState<{ id: string; kind: NodeKind } | null>(null);
  /** The line you clicked. Selecting first, deleting second: a line is a rule,
   *  and a single stray click should not quietly unwire a program. */
  const [pickedEdge, setPickedEdge] = useState<CanvasEdge | null>(null);
  const [panning, setPanning] = useState(false);
  /** Where the world sits on screen. `screen = world × zoom + pan`, and nothing
   *  bounds it — the canvas runs in every direction, including negative. */
  const [pan, setPan] = useState<Pan>({ x: WORLD_PADDING, y: WORLD_PADDING });
  const viewportRef = useRef<HTMLDivElement>(null);

  /** Where the pan was when a drag started. */
  const panRef = useRef<{ x: number; y: number; from: Pan } | null>(null);
  /** Where a line started, and whether the pointer has actually travelled —
   *  which is what separates a drag from a click on the handle. */
  const dragWireRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const dragRef = useRef<{
    id: string;
    pointerX: number;
    pointerY: number;
    origin: Point;
    last: Point;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const flags = flagged ?? new Set<string>();
  const proof = useMemo(() => evidence(program), [program]);
  /** The lines, drawn between the cards they were drawn between. */
  const graphEdges = useMemo(
    () => graph.edges.filter((e) => cardById.has(e.fromCard) && cardById.has(e.toCard)),
    [graph, cardById],
  );
  /**
   * Where each card is: its own coordinate, and nothing else.
   *
   * This used to be arithmetic over lane indices, which is why cards moved when
   * connections changed — a card's place *was* its position in a list. A card
   * now sits where it was put, and only a drag moves it.
   */
  const cardPositions = useMemo(
    () => Object.fromEntries(graph.cards.map((card) => [card.id, { x: card.x, y: card.y }])),
    [graph],
  );
  const world = useMemo(() => worldSize(cardPositions), [cardPositions]);

  /** The in-port of the card the line is over — where it will actually land. */
  const snapTarget = useMemo(() => {
    if (!wire || !wireOver) return null;
    const at = cardPositions[wireOver];
    return at ? { x: at.x + CARD_WIDTH / 2, y: at.y - PORT_LIFT } : null;
  }, [wire, wireOver, cardPositions]);

  const pick = (kind: NodeKind, node: AnyNode) => {
    setSelected({ kind, node });
    setInspectorOpen(true);
    onSelect?.(kind, node);
  };

  const activeId = selectedId ?? selected?.node.id ?? null;
  // The context has no card, so it is not in the card map — but it is still
  // selectable from its pane, and selecting it must open its panel.
  const active = activeId
    ? (entryById.get(activeId) ??
      (program.context?.id === activeId
        ? { kind: "context" as const, node: program.context }
        : undefined))
    : undefined;
  const isOn = (node: AnyNode) =>
    activeId === node.id;


  /** A command's flow, lit for one second after a jump from Layers. */
  const [flash, setFlash] = useState<{ nodes: Set<string>; edges: Set<string> } | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  /**
   * What the pill in the header counts.
   *
   * The last Validate wins, because it is the only one that actually ran the
   * gate — the document's own `violations` are whatever the server last wrote
   * there, and `untested` is a guess this component makes from the nodes it can
   * see. Reading those while a report sat on screen saying seven is how the
   * header came to say "1 to fix" over a list of seven.
   */
  const problems = reported ?? (program.violations.length || proof.untested);
  const publishable = problems === 0;

  /**
   * Change the picture: draw it now, save it once.
   *
   * The new graph is held locally so the canvas answers the gesture
   * immediately, and sent as one whole picture — one request per gesture,
   * which either lands or does not. The old shape wrote a connection as two or
   * three node saves each carrying its own `rev`, so a line touching one
   * command twice raced itself and left the document saying something nobody
   * drew.
   */
  // Draws, and nothing more. The picture is recovered from the document on
  // every read; there is no writer to send it to.
  const apply = useCallback((next: CanvasGraph) => draw(next), [draw]);

  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, node: AnyNode) => {
    if (event.button !== 0) return;
    const key = event.currentTarget.dataset.card ?? node.id;
    const origin = cardPositions[key] ?? { x: WORLD_PADDING, y: WORLD_PADDING };
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      id: key,
      pointerX: event.clientX,
      pointerY: event.clientY,
      origin,
      last: origin,
      moved: false,
    };
    setDraggingId(key);
  };

  const moveDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.currentTarget.dataset.card) return;
    const dx = (event.clientX - drag.pointerX) / zoom;
    const dy = (event.clientY - drag.pointerY) / zoom;
    // Unclamped: a node may live anywhere, including behind the origin. The
    // old floor of 24 was the scroll box's edge, and there is no edge now.
    const next = { x: Math.round(drag.origin.x + dx), y: Math.round(drag.origin.y + dy) };
    drag.last = next;
    drag.moved = drag.moved || Math.abs(dx) > 3 || Math.abs(dy) > 3;
    // Local only while the pointer is down: a save per pixel is a hundred
    // writes for one gesture.
    draw(moveCard(graph, drag.id, next));
  };

  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.currentTarget.dataset.card) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setDraggingId(null);
    if (drag.moved) suppressClickRef.current = true;
    // Saved whenever it actually moved, not only when it moved *far*: the
    // threshold is what separates a drag from a click, and a two-pixel nudge
    // is still a position the canvas is now drawing and the server has not
    // been told about.
    if (drag.last.x !== drag.origin.x || drag.last.y !== drag.origin.y) {
      apply(moveCard(graph, drag.id, drag.last));
    }
  };

  /**
   * Where a pointer or drop landed, in world coordinates.
   *
   * Not clamped to a positive quadrant: the canvas is infinite in every
   * direction, so a node put above or to the left of everything else has a
   * negative coordinate and that is a real place, not an error.
   */
  const worldPoint = (clientX: number, clientY: number, centred = false): Point | null => {
    const viewport = viewportRef.current;
    if (!viewport) return null;
    const rect = viewport.getBoundingClientRect();
    const world = toWorld({ x: clientX - rect.left, y: clientY - rect.top }, pan, zoom);
    return {
      x: Math.round(world.x - (centred ? CARD_WIDTH / 2 : 0)),
      y: Math.round(world.y - (centred ? CARD_HEIGHT / 2 : 0)),
    };
  };

  const dropFromPalette = (event: ReactDragEvent<HTMLElement>) => {
    event.preventDefault();
    const point = worldPoint(event.clientX, event.clientY, true);
    if (!point) return;


    // A component let go over open canvas: one more card of it, wired to
    // nothing yet. Every drop out of the pane is another independent place for
    // the same node — same logic, its own card, its own connections — which is
    // the whole of what a component is.
    const carried = event.dataTransfer.getData(DRAG_COMPONENT);
    const entry = entryById.get(carried || event.dataTransfer.getData(DRAG_NODE));
    if (!entry) return;
    setCarrying(null);
    apply(addCard(graph, entry.node.id, point).graph);
    pick(entry.kind, entry.node);
  };


  /**
   * Start a line.
   *
   * It works two ways on purpose. **Drag** from the handle to a card, or
   * **click** the handle and then click a card. A drag across a canvas that
   * also pans is a fussy gesture to get right, and a builder where connecting
   * only works if you hold the mouse steadily enough is a builder that looks
   * broken. The click path needs no precision at all.
   */
  // Drawing a connection by hand was how a program used to be built. Building
  // is the plugin's job now, so nothing starts a line — and with none in
  // flight, everything downstream of it is dead too.
  const startWire = () => {};

  const trackWire = (event: ReactPointerEvent<HTMLElement>) => {
    if (!wire) return;
    const start = dragWireRef.current;
    if (start && !start.moved) {
      start.moved =
        Math.abs(event.clientX - start.x) > 4 || Math.abs(event.clientY - start.y) > 4;
    }
    const at = worldPoint(event.clientX, event.clientY);
    if (at) setWire({ ...wire, at });
  };

  /** Let go over empty canvas. A drag that went somewhere is abandoned; a plain
   *  click leaves the line armed, waiting for the card you pick next. */
  const releaseWire = () => {
    if (dragWireRef.current?.moved !== false) setWire(null);
    dragWireRef.current = null;
    setWireOver(null);
  };

  /**
   * Finish a line on a card.
   *
   * One check, in one place, phrased by the rules themselves — and the server
   * makes the same one before it stores anything. Every refusal says which rule
   * bit, because "that did not work" is the least useful thing a canvas can
   * say.
   */
  const finishWire = (target: NodeEntry, targetKey: string) => {
    if (!wire) return;
    const { fromKey } = wire;
    setWire(null);
    setWireOver(null);
    if (fromKey === targetKey) return;

    const why = refusalFor(wiring, kindOf, fromKey, targetKey);
    if (why) {
      setRefused(why);
      return;
    }
    setRefused(null);
    apply(connect(graph, fromKey, targetKey));
    void target;
  };

  /**
   * A component dropped straight onto a card.
   *
   * The same line, from a gesture that does not need the component to already
   * be on the canvas: it lands as a new card of its own, wired to the card it
   * was dropped on. Dragging from the pane is how you add one to a card you can
   * see; the pull handle is how you add one to a card you are already next to.
   */
  const landComponent = (componentId: string, intoCard: string) => {
    setCarrying(null);
    const source = entryById.get(componentId);
    const into = cardById.get(intoCard);
    if (!source || !into) return;

    const sourceKind = into.kind;
    const why = refusal(sourceKind, source.kind, {
      taken: wiring.reaches.get(intoCard) ?? [],
      fed: false,
    });
    if (why) {
      setRefused(why);
      return;
    }
    setRefused(null);
    // Below the card it landed on, so a drop reads as "and then this" without
    // the author having to place it first.
    const at = cardPositions[intoCard];
    const made = addCard(graph, source.node.id, {
      x: at ? at.x : WORLD_PADDING,
      y: at ? at.y + ROW_GAP : WORLD_PADDING,
    });
    apply(connect(made.graph, intoCard, made.id));
    pick(source.kind, source.node);
  };

  /** Whether a card is somewhere the component in flight could land. */
  const landingFor = (cardId: string, kind: NodeKind): "target" | "no" | null => {
    if (!carrying) return null;
    return refusal(kind, carrying.kind, { taken: wiring.reaches.get(cardId) ?? [] })
      ? "no"
      : "target";
  };

  /**
   * Take one card off the canvas, and the lines that touched it with it.
   *
   * The node itself is untouched — it keeps its logic and its place in the
   * Components pane, and its other cards keep their own flows. Deleting the
   * node is a different act, and it lives in the pane behind a question.
   */
  const removeOneCard = (cardId: string) => {
    setPickedEdge(null);
    apply(removeCard(graph, cardId));
  };


  /** Every card and line a command's flow reaches, walking outward along the
   *  drawn connections. Instance keys, because the flow is lanes now. */
  const flowFrom = (start: string) => {
    const nodes = new Set([start]);
    const edges = new Set<string>();
    const queue = [start];
    while (queue.length > 0) {
      const at = queue.pop()!;
      for (const edge of graphEdges) {
        if (edge.fromCard !== at || edges.has(`${edge.fromCard}:${edge.toCard}`)) continue;
        edges.add(`${edge.fromCard}:${edge.toCard}`);
        if (!nodes.has(edge.toCard)) {
          nodes.add(edge.toCard);
          queue.push(edge.toCard);
        }
      }
    }
    return { nodes, edges };
  };

  /**
   * Put one card in the middle of the viewport, select it, and blink what it
   * reaches for a second.
   *
   * On the card itself, not on the bounding box of its whole flow: a command
   * with a long chain ended up near the top edge with its rules filling the
   * screen, which is not "go to that node".
   *
   * `cardKey` matters because a shared rule is several cards. Clicking it under
   * one command has to land on *that* command's copy — without it the lookup
   * used the node id, which is only a card key for a command, so every child
   * row selected the node and moved the canvas nowhere.
   */
  const centerOn = (entry: NodeEntry, cardKey?: string) => {
    const viewport = viewportRef.current;
    const key = cardKey ?? entry.node.id;
    const spot = cardPositions[key];
    const flow = flowFrom(key);
    if (viewport && spot) {
      const rect = viewport.getBoundingClientRect();
      setPan(
        whole({
          x: rect.width / 2 - (spot.x + CARD_WIDTH / 2) * zoom,
          y: rect.height / 2 - (spot.y + heightOf(key) / 2) * zoom,
        }),
      );
    }
    pick(entry.kind, entry.node);
    setFlash(flow);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 1000);
  };

  /**
   * Zoom, keeping one point under the pointer exactly where it is.
   *
   * Without the anchor the canvas zooms about its top-left corner and whatever
   * you were looking at slides off screen — the difference between zooming *in
   * on* something and merely making everything bigger.
   *
   * The scroll it needs cannot be set here: the world has not been resized yet.
   * So the anchor is recorded and applied in a layout effect, before paint.
   */
  const zoomAt = (next: number, clientX?: number, clientY?: number) => {
    const viewport = viewportRef.current;
    const settled = clampZoom(next);
    if (!viewport || settled === zoom) return;
    const rect = viewport.getBoundingClientRect();
    const at = {
      x: clientX === undefined ? rect.width / 2 : clientX - rect.left,
      y: clientY === undefined ? rect.height / 2 : clientY - rect.top,
    };
    setPan(whole(panForZoom(pan, at, zoom, settled)));
    setZoom(settled);
  };

  /**
   * The wheel, both ways.
   *
   * A trackpad pinch arrives as a wheel event with ctrlKey set — there is no
   * separate gesture for it — and a two-finger scroll arrives as a plain one.
   * Both are handled here rather than left to the browser, because the surface
   * no longer scrolls: it is transformed, so scrolling it is meaningless and
   * `deltaX/deltaY` have to move the pan instead.
   *
   * Registered by hand because it must call `preventDefault`, and React's
   * `onWheel` is passive — without this a pinch zooms the whole page.
   */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        zoomAt(zoom * Math.exp(-event.deltaY * PINCH), event.clientX, event.clientY);
        return;
      }
      // Shift turns a one-axis mouse wheel sideways, the usual convention.
      const [dx, dy] = event.shiftKey
        ? [event.deltaY || event.deltaX, 0]
        : [event.deltaX, event.deltaY];
      setPan((current) => whole({ x: current.x - dx, y: current.y - dy }));
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
    // Only when the numbers it reads change. It had no list at all, so the
    // listener was swapped on every render — a wasted subscribe per drag frame
    // for a handler whose behaviour depends on two numbers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, pan]);

  // Escape puts down a line or deselects one, the way it does everywhere else.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setWire(null);
      setPickedEdge(null);
      setZoomMenu(false);
      dragWireRef.current = null;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Drag the background to move around. Cards and controls handle their own
   *  pointer, so grabbing one never pans. */
  const startPan = (event: ReactPointerEvent<HTMLElement>) => {
    const viewport = viewportRef.current;
    if (!viewport || (event.button !== 0 && event.button !== 1)) return;
    if (wire) return; // a line is being drawn; the background is not the subject
    if ((event.target as HTMLElement).closest(OWN_POINTER)) return;
    panRef.current = { x: event.clientX, y: event.clientY, from: pan };
    setPanning(true);
    viewport.setPointerCapture(event.pointerId);
  };

  const movePan = (event: ReactPointerEvent<HTMLElement>) => {
    const start = panRef.current;
    if (!start) return;
    setPan(
      whole({
        x: start.from.x + (event.clientX - start.x),
        y: start.from.y + (event.clientY - start.y),
      }),
    );
  };

  const endPan = (event: ReactPointerEvent<HTMLElement>) => {
    const viewport = viewportRef.current;
    if (!panRef.current) return;
    panRef.current = null;
    setPanning(false);
    if (viewport?.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }
  };

  /* -- the panel's width ---------------------------------------------------- */

  /**
   * Drag the panel's edge.
   *
   * Listens on the window rather than capturing the pointer on the handle. The
   * handle is twelve pixels wide and the gesture is hundreds; element capture
   * is meant to cover exactly that, but it did not deliver a single move here,
   * and a resize that stops the moment you leave the strip is worse than none.
   */
  const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const startX = event.clientX;
    // Leftward is wider: the panel is pinned to the right edge and grows the
    // only way it can.
    const from = panelWidth;
    const move = (moved: PointerEvent) => setPanelWidth(clampPanel(from + (startX - moved.clientX)));
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      document.body.style.cursor = "";
      endResizeRef.current = null;
      setResizing(false);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    // The cursor belongs to the gesture, not to whatever is under the pointer —
    // without this it flickers to a text caret the moment it crosses the form.
    document.body.style.cursor = "col-resize";
    setResizing(true);
  };

  /** The menu's edge. Same gesture, mirrored: this panel is pinned to the left
   *  and grows rightward, so a rightward drag is wider. */
  const startMenuResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const startX = event.clientX;
    const from = menuWidth;
    const move = (moved: PointerEvent) => setMenuWidth(clampMenu(from + (moved.clientX - startX)));
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      document.body.style.cursor = "";
      endResizeRef.current = null;
      setResizing(false);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    document.body.style.cursor = "col-resize";
    endResizeRef.current = stop;
    setResizing(true);
  };

  /** Kept across sessions. Somebody who widens a panel to write a function
   *  wants it that wide the next time they write one, not every time they open
   *  a node. Written after the gesture rather than during it, so a drag is not
   *  a hundred writes to storage. */
  useEffect(() => {
    const handle = window.setTimeout(
      () => window.localStorage.setItem(PANEL_KEY, String(panelWidth)),
      300,
    );
    return () => window.clearTimeout(handle);
  }, [panelWidth]);

  useEffect(() => {
    if (menuDragWidth === null) return;
    const handle = window.setTimeout(
      () => window.localStorage.setItem(MENU_KEY, String(menuDragWidth)),
      300,
    );
    return () => window.clearTimeout(handle);
  }, [menuDragWidth]);

  /** Bring everything into view. Fits the nodes' own bounding box, because on
   *  an infinite canvas there is no world size to fit — only wherever the nodes
   *  happen to be, which may be anywhere. */
  const fitView = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    const fitted = fitToView(
      Object.values(cardPositions).filter(Boolean),
      { width: CARD_WIDTH, height: CARD_HEIGHT },
      { width: rect.width, height: rect.height },
      { min: MIN_ZOOM, max: MAX_ZOOM },
    );
    setZoom(Number(fitted.zoom.toFixed(3)));
    setPan(whole(fitted.pan));
  };

  /**
   * The zoom menu's two loose ends: it closes on the next click anywhere, and
   * the shortcuts printed beside its rows actually run.
   *
   * A shortcut written next to a command and not wired up is worse than no
   * shortcut — it is a promise the screen makes and the keyboard breaks. No
   * dependency array for the same reason the wheel handler has none: both close
   * over `zoom`, and a stale one zooms from the wrong place.
   */
  useEffect(() => {
    const away = () => setZoomMenu(false);
    const typing = () =>
      !!document.activeElement?.closest("input, textarea, select, [contenteditable]");
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "0") {
        event.preventDefault();
        zoomAt(1);
        setZoomMenu(false);
      }
      // Shift-1 is "!" on most layouts; both are checked because which one the
      // browser reports depends on the layout, not on the key that was pressed.
      if (event.shiftKey && (event.key === "!" || event.key === "1") && !typing()) {
        event.preventDefault();
        fitView();
        setZoomMenu(false);
      }
    };
    window.addEventListener("click", away);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", away);
      window.removeEventListener("keydown", onKey);
    };
    // `zoomAt` and `fitView` read the current zoom and pan, so the listeners
    // are refreshed when those change rather than on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, pan]);

  /** The rail is a set of toggles, not four ways to open the same drawer.
   *  Pressing the pane you are already looking at closes it — otherwise the only
   *  way to get the canvas back is to know that some *other* control does it. */
  const showPane = (which: "context" | "layers" | "reference") => {
    if (paletteOpen && pane === which) {
      setPaletteOpen(false);
      return;
    }
    setPaletteOpen(true);
    setPane(which);
  };

  // After the render, not during it: a card only calls these from an event,
  // which is always after the paint that gave it the props it is acting on.
  // No dependency list on purpose — the point is to refresh the closures every
  // render, and it only assigns to a ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    newest.current = {
      pick,
      hover: setWireOver,
      down: startDrag,
      move: moveDrag,
      up: endDrag,
      startWire,
      finishWire,
      land: landComponent,
      remove: removeOneCard,
    };
  });

  return (
    <div
      className={styles.shell}
      data-palette={paletteOpen || undefined}
      data-pane={paletteOpen ? pane : undefined}
    >
      {/* Three groups, and the middle one is centred on the window rather than
          on whatever is left over — so the name of the thing you are editing
          sits in the same place whatever the buttons on either side are doing. */}
      <header className={styles.bar}>
        <div className={styles.barInner}>
          {/* A workflow no sport owns has no sport page to go back to. */}
          <Link
            to={back?.href ?? (sport ? `/${sport}/scoring` : "/workflows")}
            className={styles.mark}
            aria-label={`Back to ${back?.label ?? (sport ? `${sport} scoring` : "workflows")}`}
          >
            <ArrowLeft size={14} />
          </Link>

          <div className={styles.crumbs}>
            <span className={styles.crumb}>{back?.label ?? (sport ? "Scoring" : "Workflows")}</span>
            <span className={styles.crumbSep}>/</span>
            <span className={styles.title}>{title ?? `Version ${version}`}</span>
            <span className={styles.versionTag}>v{version}</span>
          </div>

          <div className={styles.barRight}>
            <span className={`${styles.pill} ${publishable ? styles.ok : styles.bad}`}>
              <span className={styles.dot} />
              {publishable ? "Publishable" : `${problems} to fix`}
            </span>
            {toolbar}
          </div>
        </div>
      </header>

      {banner}

      <div className={styles.workspace}>
        {/* One control, because there is one thing to show. The canvas and the
            inspector are always on screen; only the node menu is worth a
            switch. */}
        <aside className={styles.toolRail} aria-label="Editor navigation">
          {/* Nodes written to be shared. Its own pane rather than a section of
              the one above, because these are not things to make — they are
              things that already exist and are waiting to be put somewhere. */}
          {/* Every command, as a list — the fastest way to find one on a big
              canvas, and a headcount of what this program can be asked to do. */}
          <button
            type="button"
            className={styles.railButton}
            data-active={(paletteOpen && pane === "layers") || undefined}
            aria-label="Layers"
            data-tip="Layers"
            onClick={() => showPane("layers")}
          >
            <Layers size={15} />
          </button>
          {/* Everything a function may touch — the publish gate's data, made
              readable. */}
          <button
            type="button"
            className={styles.railButton}
            data-active={(paletteOpen && pane === "reference") || undefined}
            aria-label="Reference"
            data-tip="Reference"
            onClick={() => showPane("reference")}
          >
            <BookOpen size={15} />
          </button>
          {/* The three above are ways of looking at this program. Context is
              not — it is one function, the same one every time, and it belongs
              to the sport rather than to anything on the canvas. */}
          <span className={styles.railDivider} />
          {/* Context is one per program and belongs to no chain, so it is not a
              card on the canvas — it is the other thing this rail opens. */}
          <button
            type="button"
            className={styles.railButton}
            data-active={paletteOpen && pane === "context" ? true : undefined}
            aria-label="Context"
            data-tip="Context"
            onClick={() => showPane("context")}
          >
            <Database size={15} />
          </button>
        </aside>

        <nav
          id="node-library"
          className={styles.palette}
          aria-label="Nodes"
          hidden={!paletteOpen}
          style={{ width: menuWidth }}
        >
          {/* The same handle the inspector has, on the edge this panel grows
              from. A pane that holds a function has to be as wide as the
              function. */}
          <div
            className={`${styles.panelGrip} ${styles.panelGripRight}`}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the menu"
            aria-valuenow={menuWidth}
            aria-valuemin={MENU_MIN}
            aria-valuemax={MENU_MAX}
            tabIndex={0}
            data-on={resizing || undefined}
            onPointerDown={startMenuResize}
            onKeyDown={(event) => {
              const step = event.shiftKey ? 48 : 12;
              if (event.key === "ArrowRight") setMenuWidth((w) => clampMenu(w + step));
              else if (event.key === "ArrowLeft") setMenuWidth((w) => clampMenu(w - step));
              else return;
              event.preventDefault();
            }}
            onDoubleClick={() =>
              setMenuWidth(MENU_DEFAULT[pane] ?? MENU_DEFAULT.nodes)
            }
          />
          <div className={styles.paletteTabs}>
            <strong>{PANE_HEAD[pane].name}</strong>
            <span>{PANE_HEAD[pane].hint}</span>
          </div>

          {/* Layers only. Ninety commands need a search; four node kinds are
              already all on screen, so the box was one more thing to skip past
              on the way to the thing you can see. */}
          {pane === "layers" && (
            <label className={styles.paletteSearch}>
              <Search size={15} />
              <input
                value={filter}
                placeholder={pane === "layers" ? "Search commands…" : "Search nodes…"}
                onChange={(event) => setFilter(event.target.value)}
              />
              {filter && (
                <button type="button" onClick={() => setFilter("")} aria-label="Clear search">
                  <X size={13} />
                </button>
              )}
            </label>
          )}

          <div
            className={`${styles.paletteScroll} ${pane === "context" ? styles.paletteScrollForm : ""}`}
          >
            {pane === "reference" ? (
              programId ? (
                <ReferencePane programId={programId} kind={active?.kind ?? null} />
              ) : (
                <p className={styles.empty}>No program loaded.</p>
              )
            ) : pane === "layers" ? (
              <>
                <div className={styles.group}>
                  <span className={styles.groupName}>Commands</span>
                  <span className={styles.groupCount}>{layers.length}</span>
                </div>
                {program.commands.length === 0 && (
                  <p className={styles.empty}>No commands yet — drag one from Nodes.</p>
                )}
                {program.commands.length > 0 && layers.length === 0 && (
                  <p className={styles.empty}>Nothing matches “{filter}”.</p>
                )}
                {layers.map((lane) => {
                  const kids = [
                    ...lane.validations.map((node) => ({ kind: "validation" as NodeKind, node })),
                    ...lane.triggers.map((node) => ({ kind: "trigger" as NodeKind, node })),
                  ];
                  const open = searching || openLayers.has(lane.command.id);
                  return (
                    <div key={lane.command.id} className={styles.layer}>
                      <div
                        className={styles.layerRow}
                        data-on={activeId === lane.command.id || undefined}
                      >
                        <button
                          type="button"
                          className={styles.twisty}
                          data-open={open || undefined}
                          disabled={kids.length === 0 || searching}
                          aria-expanded={open}
                          aria-label={open ? `Collapse ${lane.command.code}` : `Expand ${lane.command.code}`}
                          onClick={() =>
                            setOpenLayers((current) => {
                              const next = new Set(current);
                              if (!next.delete(lane.command.id)) next.add(lane.command.id);
                              return next;
                            })
                          }
                        >
                          <ChevronRight size={12} />
                        </button>
                        <button
                          type="button"
                          className={styles.layerName}
                          title={`Jump to ${lane.command.code}`}
                          onClick={() => centerOn({ kind: "command", node: lane.command })}
                        >
                          <span className={`${styles.itemIcon} ${styles.command}`}>
                            <KindIcon kind="command" size={13} />
                          </span>
                          <strong>{displayName(lane.command)}</strong>
                          {kids.length > 0 && (
                            <span className={styles.layerCount}>{kids.length}</span>
                          )}
                        </button>
                      </div>

                      {open && (
                        <div className={styles.layerKids}>
                          {kids.map(({ kind, node }) => (
                            <button
                              key={`${lane.command.id}:${node.id}`}
                              type="button"
                              className={`${styles.layerRow} ${styles.layerName} ${styles.layerChild}`}
                              data-on={activeId === node.id || undefined}
                              title={`Jump to ${node.code}`}
                              // A node may be several cards. Jump to the one in
                              // this command's flow, which is the row you
                              // clicked — not merely to the node.
                              onClick={() =>
                                centerOn(
                                  { kind, node },
                                  cardInFlow(graph, cardById, lane.command.id, node.id),
                                )
                              }
                            >
                              <span className={`${styles.itemIcon} ${styles[kind]}`}>
                                <KindIcon kind={kind} size={13} />
                              </span>
                              <strong>{displayName(node)}</strong>
                            </button>
                          ))}
                          {/* A reference the command names and no node declares.
                              It is a publish failure, and the canvas cannot draw
                              a card for something that is not there — so this is
                              the only place it can be seen. */}
                          {lane.missing.map((ref) => (
                            <span
                              key={`${lane.command.id}:missing:${ref}`}
                              className={`${styles.layerRow} ${styles.layerChild} ${styles.layerMissing}`}
                            >
                              <strong>{ref}</strong>
                              <small>missing</small>
                            </span>
                          ))}
                          {kids.length === 0 && lane.missing.length === 0 && (
                            <span className={`${styles.layerRow} ${styles.layerChild} ${styles.layerEmpty}`}>
                              Nothing runs on this command yet
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ) : pane === "context" ? (
              <ContextPane node={program.context} editorFor={editorFor} />
            ) : null}
          </div>
        </nav>

        <main
          id="scoring-canvas"
          ref={viewportRef}
          className={styles.canvas}
          data-panning={panning || undefined}
          // The grid moves and scales with the world. Without this, panning
          // across empty space looks like nothing is happening at all.
          style={
            {
              backgroundSize: `${DOT_GRID * zoom}px ${DOT_GRID * zoom}px`,
              backgroundPosition: `${pan.x}px ${pan.y}px`,
            } as CSSProperties
          }
          /* preventDefault says "a drop may land here"; `dropEffect` says which
             kind. Both are needed, and leaving the second out is not a default
             — it is a mismatch. A component's drag declares
             `effectAllowed = "link"`, Chrome's unset `dropEffect` is "copy",
             and a copy that the source never allowed is refused: the drop
             simply never fires and the drag reads as broken.

             `types` is the one thing readable during a dragover — the payload
             is not — which is exactly enough to tell which kind of drag this
             is. */
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = event.dataTransfer.types.includes(DRAG_COMPONENT)
              ? "link"
              : "copy";
          }}
          onDrop={dropFromPalette}
          onPointerDown={(event) => {
            const target = event.target as HTMLElement;
            // The menu is part of the picked line. Dismissing on any press
            // outside the line used to include a press on the menu's own
            // Delete button — the menu unmounted on pointerdown and the click
            // landed on nothing, so deleting a connection silently did nothing.
            if (!target.closest("[data-edge], [data-edge-menu]")) setPickedEdge(null);
            // Pressing bare canvas puts down whatever was in hand.
            if (wire && !target.closest("[data-node-id]")) {
              setWire(null);
              dragWireRef.current = null;
            }
            startPan(event);
          }}
          onPointerMove={(event) => {
            movePan(event);
            trackWire(event);
          }}
          onPointerUp={(event) => {
            endPan(event);
            releaseWire();
          }}
          onPointerCancel={(event) => {
            endPan(event);
            setWire(null);
            dragWireRef.current = null;
          }}
        >
          <div className={styles.canvasTools}>
            <div className={styles.zoomBar}>
              <button
                type="button"
                className={styles.zoomStep}
                aria-label="Zoom out"
                disabled={zoom <= MIN_ZOOM + 0.001}
                onClick={() => zoomAt(zoom / WHEEL_STEP)}
              >
                <Minus size={15} />
              </button>
              {/* The reading is the control. Clicking it opens the levels
                  rather than jumping straight to 100% — the old button did
                  that silently, so the only way to find out what it did was to
                  lose your place. */}
              <button
                type="button"
                className={styles.zoomLevel}
                aria-haspopup="menu"
                aria-expanded={zoomMenu}
                aria-label={`Zoom, ${Math.round(zoom * 100)} per cent`}
                onClick={(event) => {
                  event.stopPropagation();
                  setZoomMenu((open) => !open);
                }}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className={styles.zoomStep}
                aria-label="Zoom in"
                disabled={zoom >= MAX_ZOOM - 0.001}
                onClick={() => zoomAt(zoom * WHEEL_STEP)}
              >
                <Plus size={15} />
              </button>

              {zoomMenu && (
                <div className={styles.zoomMenu} role="menu">
                  {ZOOM_PRESETS.map((preset) => {
                    const Icon = preset.icon;
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        role="menuitem"
                        data-on={Math.round(zoom * 100) === preset.value || undefined}
                        onClick={() => {
                          zoomAt(preset.value / 100);
                          setZoomMenu(false);
                        }}
                      >
                        <Icon size={13} />
                        {preset.value}%
                        {preset.shortcut && (
                          <span className={styles.shortcut}>{preset.shortcut}</span>
                        )}
                      </button>
                    );
                  })}
                  <span className={styles.zoomMenuDivider} />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      fitView();
                      setZoomMenu(false);
                    }}
                  >
                    <Maximize size={13} />
                    Zoom to fit
                    <span className={styles.shortcut}>⇧1</span>
                  </button>
                </div>
              )}
            </div>

            {/* Its own button, away from the stepper: fitting is not a step in
                the same direction, it is giving up on the number entirely. */}
            <button
              type="button"
              className={styles.fitButton}
              aria-label="Fit workflow"
              data-tip="Zoom to fit"
              onClick={fitView}
            >
              <Maximize size={15} />
            </button>
          </div>

          {wire && (
            <p className={styles.lensNote}>
              Drawing from <strong>{wire.from.node.code}</strong> — drop it on a highlighted card,
              or click one. Escape to put it down.
            </p>
          )}

          {refused && (
            <p className={styles.refused} role="status">
              {refused}
              <button type="button" onClick={() => setRefused(null)} aria-label="Dismiss">
                ×
              </button>
            </p>
          )}

          {/* One transformed layer, not a scroll box. `translate` before
              `scale` so the pan is in screen pixels and the world beneath it
              keeps its own coordinates whatever the zoom. */}
          <div
            className={styles.surface}
            style={
              {
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              } as CSSProperties
            }
          >
            <svg className={styles.edges} width={world.width} height={world.height}>
                <defs>
                  {/* Two strokes meeting at a point, not a filled triangle — the
                      head should read as the wire arriving, the way an arrow is
                      drawn by hand. Stroked in the wire's own colour by
                      `.edges marker path`.

                      Sized in user units, not stroke widths: the default ties a
                      marker to the line's weight, so lighting a flow thickened
                      the wires *and* blew the heads up with them. */}
                  <marker
                    id="scoring-arrow"
                    viewBox="0 0 10 10"
                    refX="8"
                    refY="5"
                    markerUnits="userSpaceOnUse"
                    markerWidth="11"
                    markerHeight="11"
                    orient="auto-start-reverse"
                  >
                    <path d="M 2.4 1 L 8 5 L 2.4 9" />
                  </marker>
                </defs>
                {graphEdges.map((edge) => {
                  const from = cardPositions[edge.fromCard];
                  const to = cardPositions[edge.toCard];
                  if (!from || !to) return null;
                  const cuttable = false;
                  const on =
                    pickedEdge?.fromCard === edge.fromCard && pickedEdge?.toCard === edge.toCard;
                  return (
                    <g
                      key={`${edge.fromCard}-${edge.toCard}`}
                      className={styles.edge}
                      /* Which two cards this line joins. Carried on the element
                         because otherwise the only way to check the picture
                         matches the document is to look at it — and a line that
                         starts on the wrong card looks like a line. */
                      data-from={edge.fromCard}
                      data-to={edge.toCard}
                      data-on={on || undefined}
                      data-flash={flash?.edges.has(`${edge.fromCard}:${edge.toCard}`) || undefined}
                    >
                      <path
                        d={edgePath(from, to, heightOf(edge.fromCard))}
                        markerEnd="url(#scoring-arrow)"
                      />
                      {cuttable && (
                        <path
                          className={styles.edgeHit}
                          data-edge="true"
                          d={edgePath(from, to, heightOf(edge.fromCard))}
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={() => setPickedEdge(on ? null : edge)}
                        >
                          <title>{describeEdge(cardById, edge)}</title>
                        </path>
                      )}
                    </g>
                  );
                })}
                {wire && (
                  <path
                    className={styles.wire}
                    /* Snapped to the card the head is over, so the join you
                       are about to make is drawn before you commit to it —
                       otherwise the line ends at the pointer and you find out
                       where it landed by letting go. */
                    d={wirePath(
                      cardPositions[wire.fromKey],
                      snapTarget ?? wire.at,
                      heightOf(wire.fromKey),
                    )}
                    markerEnd="url(#scoring-arrow)"
                  />
                )}
              </svg>

              {/* The chosen line's delete button, sitting on the line. HTML
                  rather than SVG so it is a real button — focusable, and it
                  looks like every other control on the page. */}
              {pickedEdge &&
                cardPositions[pickedEdge.fromCard] &&
                cardPositions[pickedEdge.toCard] &&
                (() => {
                  const at = edgeMidpoint(
                    cardPositions[pickedEdge.fromCard],
                    cardPositions[pickedEdge.toCard],
                    heightOf(pickedEdge.fromCard),
                  );
                  return (
                    <div
                      className={styles.edgeMenu}
                      data-edge-menu="true"
                      style={{ left: at.x, top: at.y }}
                    >
                      <span>{describeEdge(cardById, pickedEdge)}</span>
                      <button
                        type="button"
                        onClick={() => {
                          // Only the line. Both cards stay exactly where they
                          // are, because a card's place is its own coordinate
                          // and cutting a line does not touch it.
                          apply(disconnect(graph, pickedEdge.fromCard, pickedEdge.toCard));
                          setPickedEdge(null);
                        }}
                      >
                        <Trash2 size={12} />
                        Delete
                      </button>
                    </div>
                  );
                })()}

              {cards.map((card) => {
                const point = cardPositions[card.card.id];
                if (!point) return null;
                // A line in flight: whether it could land here, said before it
                // is let go rather than after. The same rules that would refuse
                // it, so what lights up and what is accepted cannot disagree.
                const takes = wire
                  ? refusalFor(wiring, kindOf, wire.fromKey, card.card.id)
                    ? "no"
                    : wireOver === card.card.id
                      ? "over"
                      : "target"
                  : null;
                return (
                  <NodeCard
                    key={card.card.id}
                    cardKey={card.card.id}
                    kind={card.kind}
                    node={card.node}
                    /* Two numbers, not a point. A card's props are compared to
                       decide whether to redraw it, and a fresh `{x, y}` every
                       render is never equal to the last one — so one card
                       moving redrew every card in the program. */
                    x={point.x}
                    y={point.y}
                    on={isOn(card.node)}
                    flagged={flags.has(card.node.id)}
                    flashing={flash?.nodes.has(card.card.id) ?? false}
                    dragging={draggingId === card.card.id}
                    dimmed={!appliesTo(card.node, lens)}
                    // A card no flow reaches: nothing runs it, and it is what
                    // stops a publish — so it says so on the canvas rather than
                    // only in the report.
                    loose={wiring.loose.has(card.card.id)}
                    wiring={wire && wire.fromKey === card.card.id ? "no" : takes}
                    landing={landingFor(card.card.id, card.kind)}
                    armed={wire?.fromKey === card.card.id}
                    deletable={false}
                    deleteTitle={
                      (cardsPerNode.get(card.node.id) ?? 1) > 1
                        ? "Take this card off the canvas — the node keeps its other cards"
                        : card.node.component
                          ? "Take it off the canvas — it stays in Components, ready to place again"
                          : "Take it off the canvas — this is its last card, so the node goes too"
                    }
                    on_={handlers}
                    suppressClickRef={suppressClickRef}
                  />
                );
              })}
          </div>
        </main>

        <aside
          id="node-inspector"
          className={styles.inspector}
          hidden={!(inspectorOpen && active)}
          style={{ width: panelWidth }}
        >
          {/* The handle, on the edge the panel grows from. A function is code,
              code is as wide as it is, and a fixed panel makes the author
              scroll sideways through their own rule. */}
          <div
            className={styles.panelGrip}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the panel"
            aria-valuenow={panelWidth}
            aria-valuemin={PANEL_MIN}
            aria-valuemax={PANEL_MAX}
            tabIndex={0}
            data-on={resizing || undefined}
            onPointerDown={startResize}
            onKeyDown={(event) => {
              // A separator you can only drag is a separator a keyboard cannot
              // reach, and this one decides how much of the screen the work gets.
              const step = event.shiftKey ? 48 : 12;
              if (event.key === "ArrowLeft") setPanelWidth((w) => clampPanel(w + step));
              else if (event.key === "ArrowRight") setPanelWidth((w) => clampPanel(w - step));
              else return;
              event.preventDefault();
            }}
            onDoubleClick={() => setPanelWidth(PANEL_DEFAULT)}
          />
          {/* The scrolling happens here, not on the panel, so the handle above
              stays where it is however far down the form you are. */}
          <div className={styles.inspectorScroll}>
            {active ? (
              <Inspector
                selected={{ kind: active.kind, node: active.node }}
                program={program}
                editorFor={editorFor}
                onClose={() => setInspectorOpen(false)}
              />
            ) : (
              <div className={styles.blank}>
                <Workflow size={24} />
                <strong>Select a node</strong>
                <span>Its scoring rules and test controls will appear here.</span>
              </div>
            )}
          </div>
        </aside>
      </div>

      <footer className={styles.statusBar}>
        <span className={styles.runtimeState} data-live={undefined}>
          <span />
          Published version
        </span>
        <span className={styles.statusItem}>
          <Box size={12} />
          {nodeEntries.length} nodes
        </span>
        <span className={styles.statusItem}>
          <CircleDot size={12} />
          {proof.tests} test{proof.tests === 1 ? "" : "s"}
        </span>
        <span className={styles.statusSpacer} />
        <span className={styles.statusItem}>
          <History size={12} />
          Generated from the flows plugin
        </span>
      </footer>
    </div>
  );
}

/**
 * The context, beside the canvas rather than on it.
 *
 * One per program, and part of no chain: it is the single function that gathers
 * everything a scoring screen reads. A node on the canvas would have implied it
 * runs somewhere in the flow, which it does not — it is asked, not fired.
 */
function ContextPane({
  node,
  editorFor,
}: {
  node: ContextNode | null;
  editorFor?: (kind: NodeKind, node: AnyNode) => ReactNode;
}) {
  if (!node) {
    return (
      <>
        <p className={styles.empty}>
          Nothing yet. The context is one function that returns whatever a scoring screen needs —
          the scorecard, the card, the live table — so the screen does not have to know the sport.
        </p>
      </>
    );
  }
  // The function itself, here. It used to be a button that opened the node in
  // the inspector on the far side of the screen — one click and a jump across
  // the window to reach the only thing this pane is about.
  return <div className={styles.contextForm}>{editorFor?.("context", node)}</div>;
}



/* -------------------------------------------------------------------- cards */

const NodeCard = memo(function NodeCard({
  cardKey,
  kind,
  node,
  x,
  y,
  on,
  flagged,
  flashing,
  dragging,
  dimmed,
  wiring,
  armed,
  deletable,
  deleteTitle,
  suppressClickRef,
  landing,
  loose,
  on_,
}: {
  /** The instance key — which of a component's cards this one is. */
  cardKey: string;
  kind: NodeKind;
  node: AnyNode;
  x: number;
  y: number;
  on: boolean;
  /** Anchors own their position. An instance is projected below its command
   *  and cannot be dragged on its own. */
  flagged?: boolean;
  /** Part of the flow a Layers jump just lit up. */
  flashing?: boolean;
  dragging: boolean;
  /** Outside the current lens — visible, so its existence is not a surprise,
   *  but faded, because this fixture would never be offered it. */
  dimmed?: boolean;
  /** Whether a line being drawn could land here. Null when none is. */
  wiring: "over" | "target" | "no" | null;
  /** Whether the line in flight started from this card. */
  armed?: boolean;
  /** Its one line out is already drawn — the pull handle goes dead. */
  /** Whether this card's bin does anything — a published program has none. */
  deletable: boolean;
  deleteTitle: string;
  suppressClickRef: { current: boolean };
  /** Whether a component being dragged out of its pane could land on this card.
   *  Null when nothing is being carried. */
  landing: "target" | "no" | null;
  /** No flow reaches this card. Nothing runs it, and it blocks a publish. */
  loose?: boolean;
  /** The canvas's handlers — one object, the same one every render. */
  on_: CardHandlers;
}) {
  const meta = nodeMeta(kind, node);
  const tag = nodeTag(kind, node);
  const entry: NodeEntry = { kind, node };
  return (
    <button
      className={`${styles.node} ${styles[kind]}`}
      style={{ left: x, top: y }}
      data-node-id={node.id}
      data-card={cardKey}
      data-on={on || undefined}
      data-flagged={flagged || undefined}
      data-flash={flashing || undefined}
      data-dragging={dragging || undefined}
      data-dimmed={dimmed || undefined}
      data-wiring={wiring ?? undefined}
      data-landing={landing ?? undefined}
      data-loose={loose || undefined}
      /* A component dropped from its pane. `onDragOver` has to preventDefault
         or the browser refuses the drop outright — and it is also what says
         "yes, here", so a card that cannot take this one simply does not. */
      onDragOver={
        landing === "target"
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              event.dataTransfer.dropEffect = "link";
            }
          : undefined
      }
      onDrop={
        landing === "target"
          ? (event) => {
              event.preventDefault();
              // Without this the canvas below also handles the drop, and its
              // handler reads a different data type and repositions a card.
              event.stopPropagation();
              const carried = event.dataTransfer.getData(DRAG_COMPONENT);
              if (carried) on_.land(carried, cardKey);
            }
          : undefined
      }
      /* `pointermove`, not `pointerenter`. While a button is held the browser
         does not reliably report entering an element — which is exactly and
         only when this matters. Move fires the whole time the head is over the
         card, so the highlight follows the pointer instead of waiting for a
         crossing that never comes. */
      onPointerLeave={wiring ? () => on_.hover(null) : undefined}
      onPointerMove={(event) => {
        // Only a card that can take the line. A card that cannot used to claim
        // the hover anyway, which snapped the line to its port and then hid the
        // last stretch of it behind the card — so the answer to "can I drop
        // here?" was the line disappearing.
        if (wiring === "target" || wiring === "over") on_.hover(cardKey);
        else if (wiring) on_.hover(null);
        on_.move(event);
      }}
      onPointerUp={(event) => {
        // Always end the move first. A line can be *armed* by a click and then
        // stay armed, so a press-and-drag on some other card ends with a
        // release that also finishes the line — and returning early there left
        // the pointer captured and the drag state set, which glued the card to
        // the pointer with no button down and never saved where it went.
        on_.up(event);
        if (wiring && !armed) on_.finishWire(entry, cardKey);
      }}
      /* A drag the browser takes away — a touch becoming a scroll, this card
         being removed — otherwise leaves the same stuck state as above. */
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        // A line left armed by a click on the handle lands here.
        if (wiring && !armed) {
          on_.finishWire(entry, cardKey);
          return;
        }
        on_.pick(kind, node);
      }}
    >
      {/* The kind, lifted out of the card and set above it.

          It used to be the first line *inside*, which meant every card opened
          with a word that never changes and the name — the thing you are
          actually scanning for — started on line two. Above the card it is a
          label on the box rather than its first fact, and it costs no layout: it
          floats in the gap the rows already leave. */}
      <span className={styles.nodeTag} data-kind={kind}>
        {KIND_TAG[kind]}
      </span>
      <span className={styles.nodeHead}>
        <span className={styles.nodeIdentity}>
          <span className={styles.nodeIcon}>
            <KindIcon kind={kind} size={14} />
          </span>
          <span>{displayName(node)}</span>
        </span>
        {deletable && (
          <span
            className={styles.nodeDelete}
            role="button"
            tabIndex={-1}
            aria-label={`Delete ${node.code}`}
            title={deleteTitle}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              on_.remove(cardKey);
            }}
          >
            <Trash2 size={13} />
          </span>
        )}
      </span>
      <span className={styles.nodeRule} />
      {node.description?.trim() && (
        <>
          <span className={styles.nodeDescription}>{node.description}</span>
          <span className={styles.nodeRuleLower} />
        </>
      )}
      {(tag || node.component || node.appliesTo.length > 0) && (
        <span className={styles.kind}>
          {/* Shared. In the chip row rather than floating above the card,
              because it is a fact about this node like the others beside it —
              and because the strip above the card is the kind chip's, and two
              labels up there read as two cards touching. */}
          {node.component && (
            <span className={styles.nodeShared} title="A component — shared by commands">
              <Blocks size={9} />
              Component
            </span>
          )}
          {tag && <span className={styles.tag}>{tag}</span>}
          {node.appliesTo.length > 0 && (
            <span className={styles.only} title="Only offered to these">
              {node.appliesTo.join(" · ")}
            </span>
          )}
        </span>
      )}
      {meta && <span className={styles.meta}>{meta}</span>}
      {/* A command is where a flow starts — nothing runs before it, so there is
          nothing to wire into. Drawing a socket that can never take a line is an
          invitation to try. */}
      {kind !== "command" && <span className={styles.portIn} />}
      {/* The port is drawn, never pulled. Connections come from the program
          document — which command runs which rule — and that is the plugin's
          to say, so there is nothing here to take hold of. */}
      <span className={styles.portOut} />
    </button>
  );
});

/* Memoised because a canvas is many cards and a drag is many frames: without
   it, moving one card re-rendered every other card in the program on every
   pointer move. The props are all primitives, functions from `useCallback`, or
   the node itself, so the default shallow compare is the right one. */

function KindIcon({ kind, size }: { kind: NodeKind; size: number }) {
  if (kind === "command") return <Zap size={size} />;
  if (kind === "validation") return <ShieldCheck size={size} />;
  if (kind === "compute") return <Braces size={size} />;
  if (kind === "trigger") return <CircleDot size={size} />;
  return <Database size={size} />;
}

/** What the canvas draws. Context is not here: there is one per program, it is
 *  part of no chain, and it lives beside the canvas rather than on it. */
function entries(program: ScoringProgram): NodeEntry[] {
  return [
    ...program.commands.map((node) => ({ kind: "command" as const, node })),
    ...program.validations.map((node) => ({ kind: "validation" as const, node })),
    ...program.triggers.map((node) => ({ kind: "trigger" as const, node })),
    ...program.computes.map((node) => ({ kind: "compute" as const, node })),
  ];
}


/**
 * Where a line's own controls sit.
 *
 * The bezier's midpoint, which works out to the plain average of its ends: the
 * two control points bend by equal and opposite amounts, so at t=0.5 the bend
 * cancels exactly.
 */
function edgeMidpoint(from: Point, to: Point, fromHeight: number): Point {
  return {
    x: (from.x + to.x) / 2 + CARD_WIDTH / 2,
    y: (from.y + fromHeight + to.y) / 2,
  };
}

/** What a line says, in the author's words: this, then that. */
function describeEdge(
  cards: Map<string, { kind: NodeKind; node: AnyNode }>,
  edge: CanvasEdge,
): string {
  const from = cards.get(edge.fromCard);
  const to = cards.get(edge.toCard);
  if (!from || !to) return "a line";
  return `${displayName(from.node)} → ${displayName(to.node)}`;
}

/**
 * Which card of a node sits in this command's flow.
 *
 * A node may be drawn several times, and the Layers row you clicked is one
 * command's use of it. Walking the flow is how the answer is found, because a
 * card carries no lane any more — it is wherever the lines put it.
 */
function cardInFlow(
  graph: CanvasGraph,
  cards: Map<string, { kind: NodeKind; node: AnyNode }>,
  commandId: string,
  nodeId: string,
): string | undefined {
  const out = new Map<string, string[]>();
  for (const edge of graph.edges) {
    out.set(edge.fromCard, [...(out.get(edge.fromCard) ?? []), edge.toCard]);
  }
  const start = graph.cards.find((card) => card.node === commandId);
  if (!start) return undefined;
  const seen = new Set([start.id]);
  const queue = [start.id];
  while (queue.length > 0) {
    const at = queue.shift()!;
    if (cards.get(at)?.node.id === nodeId) return at;
    for (const next of out.get(at) ?? []) {
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  return undefined;
}

/** The line in flight, from a card's out-port to wherever the pointer is. */
function wirePath(from: Point | undefined, to: Point, fromHeight: number): string {
  if (!from) return "";
  const startX = from.x + CARD_WIDTH / 2;
  const startY = from.y + fromHeight;
  const bend = Math.max(32, Math.abs(to.y - startY) * 0.42);
  return `M ${startX} ${startY} C ${startX} ${startY + bend}, ${to.x} ${to.y - bend}, ${to.x} ${to.y}`;
}

function edgePath(from: Point, to: Point, fromHeight: number): string {
  const startX = from.x + CARD_WIDTH / 2;
  const startY = from.y + fromHeight;
  const endX = to.x + CARD_WIDTH / 2;
  const endY = to.y - PORT_LIFT;
  const bend = Math.max(48, Math.abs(endY - startY) * 0.42);
  return `M ${startX} ${startY} C ${startX} ${startY + bend}, ${endX} ${endY - bend}, ${endX} ${endY}`;
}

/**
 * How big to make the edge layer.
 *
 * Not the size of the world — the world has no size. This only has to be an
 * `<svg>` big enough that browsers keep painting inside it; anything outside
 * still draws, because the layer is `overflow: visible`. A generous floor keeps
 * that true for a program laid out behind the origin.
 */
function worldSize(positions: Record<string, Point>): { width: number; height: number } {
  // Folded rather than spread. `Math.max(...points)` passes every card as an
  // argument, and a long enough argument list overflows the stack — a canvas
  // that throws instead of drawing, at a size that is only unreachable because
  // something else caps it first.
  let width = 2000;
  let height = 1400;
  for (const point of Object.values(positions)) {
    width = Math.max(width, point.x + CARD_WIDTH + WORLD_PADDING);
    height = Math.max(height, point.y + CARD_HEIGHT + WORLD_PADDING);
  }
  return { width, height };
}

function displayName(node: AnyNode): string {
  if (node.label?.trim()) return node.label;
  return node.code
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * The chip inside the card, when there is one worth having.
 *
 * Only where the value varies between two cards of the same kind: sync against
 * async, an expression against a function. A compute node is always computed
 * and a command is nearly always an append, so those chips were a word that
 * never told you anything in the space the description now uses.
 */
function nodeTag(kind: NodeKind, node: AnyNode): string | null {
  if (kind === "trigger") return (node as TriggerNode).mode;
  if (kind === "context") return "read model";
  if (kind === "validation") {
    const checks = (node as ValidationNode).checks ?? [];
    // Plural when it is: how many things this rule tests is the whole reason
    // one rule can now stand for what used to be three nodes.
    if (checks.length > 1) return `${checks.length} expressions`;
    return checks.length ? "expression" : "function";
  }
  return null;
}

/** The line under the chips. Empty where the description says it better. */
function nodeMeta(kind: NodeKind, node: AnyNode): string {
  if (kind === "command") {
    const command = node as CommandNode;
    const count = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;
    return `${count(countFields(command.input), "Input")} · ${count(command.validations.length, "Rule")} · ${count(command.triggers.length, "Action")}`;
  }
  if (kind === "validation" || kind === "compute") return "";
  if (kind === "trigger") {
    const trigger = node as TriggerNode;
    return trigger.owns ? `Writes ${trigger.owns.target}` : "No output target";
  }
  return `Reads ${(node as ContextNode).reads.join(", ") || "program state"}`;
}

/* ---------------------------------------------------------------- inspector */

function Inspector({
  selected,
  program,
  editorFor,
  onClose,
}: {
  selected: Selected;
  program: ScoringProgram;
  editorFor?: (kind: NodeKind, node: AnyNode) => ReactNode;
  onClose?: () => void;
}) {
  const { kind, node } = selected;
  const problems = violationsFor(program.violations, node.id);
  return (
    <>
      <div className={styles.inspectorHead}>
        <span className={`${styles.inspectorIcon} ${styles[kind]}`}>
          <KindIcon kind={kind} size={15} />
        </span>
        <div className={styles.inspectorHeadText}>
          <h3 className={styles.inspectorCode}>{displayName(node)}</h3>
          <p className={styles.inspectorKind}>{KIND_TAG[kind]}</p>
        </div>
        {/* In the head rather than floating over the panel: the head is sticky,
            so the way out stays reachable however far down you have scrolled —
            and `top: 50%` on a scrolling panel means half way down the *panel*,
            which is where it briefly went. */}
        {onClose && (
          <button
            type="button"
            className={styles.inspectorClose}
            aria-label="Close the panel"
            title="Close"
            onClick={onClose}
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Always shown: a reason a program cannot publish must never hide. */}
      {problems.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionName}>Cannot publish</div>
          {problems.map((p, i) => (
            <p key={i} className={styles.prose}>
              {p.message}
            </p>
          ))}
        </section>
      )}

      {editorFor && <section className={styles.section}>{editorFor(kind, node)}</section>}
    </>
  );
}

/* ------------------------------------------------------------------ helpers */

function countFields(schema: JsonSchema): number {
  return Object.keys(schema.properties ?? {}).length;
}
